/**
 * Interceptor manager for handling request/response lifecycle hooks
 */

import type {
  Interceptor,
  RequestContext,
  RequestErrorContext,
  ResponseContext,
  ResponseErrorContext,
  ValidateStatusContext,
} from './types';

/**
 * Manages a collection of interceptors and executes their hooks
 */
export class InterceptorManager {
  private interceptors: Interceptor[] = [];

  /**
   * Add an interceptor
   * @param interceptor Interceptor to add
   * @returns Unsubscribe function to remove the interceptor
   */
  add(interceptor: Interceptor): () => void {
    this.interceptors.push(interceptor);

    // Return unsubscribe function
    return () => {
      const index = this.interceptors.indexOf(interceptor);
      if (index > -1) {
        this.interceptors.splice(index, 1);
      }
    };
  }

  /**
   * Execute all onRequest hooks
   */
  async executeOnRequest(context: RequestContext): Promise<void> {
    for (const interceptor of this.interceptors) {
      if (interceptor.onRequest) {
        await interceptor.onRequest(context);
      }
    }
  }

  /**
   * Execute all onRequestError hooks
   */
  async executeOnRequestError(context: RequestErrorContext): Promise<void> {
    for (const interceptor of this.interceptors) {
      if (interceptor.onRequestError) {
        await interceptor.onRequestError(context);
      }
    }
  }

  /**
   * Execute all onResponse hooks
   */
  async executeOnResponse<T = any>(context: ResponseContext<T>): Promise<void> {
    for (const interceptor of this.interceptors) {
      if (interceptor.onResponse) {
        await interceptor.onResponse(context);
      }
    }
  }

  /**
   * Execute all onResponseError hooks
   */
  async executeOnResponseError<T = any>(context: ResponseErrorContext<T>): Promise<void> {
    for (const interceptor of this.interceptors) {
      if (interceptor.onResponseError) {
        await interceptor.onResponseError(context);
      }
    }
  }

  /**
   * Execute all validateStatus hooks
   * Returns true if all validators pass (or if no validators exist)
   */
  validateStatus<T = any>(context: ValidateStatusContext<T>): boolean {
    const validators = this.interceptors
      .map((i) => i.validateStatus)
      .filter((v): v is NonNullable<typeof v> => !!v);

    // If no validators, use default behavior
    if (validators.length === 0) {
      return true;
    }

    // All validators must return true
    return validators.every((validator) => validator(context));
  }
}
